/**
 * AskSection — the fundraise ask with fund allocation pie + hiring plan.
 */
import { motion } from "framer-motion";
import { DollarSign, Users, Target } from "lucide-react";
import PitchSection from "../PitchSection";
import SectionLabel from "../SectionLabel";

const sliceColors = ["#3B82F6", "#4FA9A1", "#A78BFA", "#F59E0B"];

export default function AskSection({ data }) {
  // SVG donut chart
  const total = data.useOfFunds.reduce((s, f) => s + f.pct, 0);
  let cumulative = 0;
  const slices = data.useOfFunds.map((fund, i) => {
    const startAngle = (cumulative / total) * 360;
    cumulative += fund.pct;
    const endAngle = (cumulative / total) * 360;
    const startRad = ((startAngle - 90) * Math.PI) / 180;
    const endRad = ((endAngle - 90) * Math.PI) / 180;
    const largeArc = endAngle - startAngle > 180 ? 1 : 0;
    const r = 80;
    const cx = 100, cy = 100;
    const x1 = cx + r * Math.cos(startRad);
    const y1 = cy + r * Math.sin(startRad);
    const x2 = cx + r * Math.cos(endRad);
    const y2 = cy + r * Math.sin(endRad);
    return {
      path: `M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2} Z`,
      color: sliceColors[i % sliceColors.length],
      ...fund,
    };
  });

  return (
    <PitchSection id="ask" dark>
      <SectionLabel>{data.label}</SectionLabel>
      <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4 leading-tight">
        {data.title}
      </h2>

      {/* Big number */}
      <div className="flex items-center gap-4 mb-10">
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          whileInView={{ scale: 1, opacity: 1 }}
          viewport={{ once: true }}
          className="flex items-center gap-3 bg-gradient-to-r from-[#3B82F6]/15 to-[#4FA9A1]/15 border border-white/10 rounded-2xl px-4 sm:px-6 py-4 max-w-full"
        >
          <DollarSign className="w-8 h-8 text-[#4FA9A1] shrink-0" />
          <div className="min-w-0">
            <p className="text-3xl sm:text-4xl font-extrabold text-white">{data.raising}</p>
            <p className="text-sm sm:text-base text-gray-400 break-words">{data.instrument} — {data.runway}</p>
          </div>
        </motion.div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-10">
        {/* Use of Funds with donut */}
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
        >
          <h3 className="text-white font-semibold mb-6 flex items-center gap-2">
            <Target className="w-5 h-5 text-[#3B82F6]" />
            Use of Funds
          </h3>

          <div className="flex flex-col sm:flex-row items-center gap-6">
            {/* Donut */}
            <svg viewBox="0 0 200 200" className="w-32 sm:w-36 md:w-40 h-32 sm:h-36 md:h-40 flex-shrink-0">
              {slices.map((s, i) => (
                <motion.path
                  key={i}
                  d={s.path}
                  fill={s.color}
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="hover:opacity-80 transition-opacity cursor-pointer"
                />
              ))}
              {/* Center hole */}
              <circle cx="100" cy="100" r="45" fill="#070B14" />
              <text x="100" y="96" textAnchor="middle" className="fill-white text-xs font-bold" fontSize="14">
                {data.raising}
              </text>
              <text x="100" y="112" textAnchor="middle" className="fill-gray-400" fontSize="9">
                {data.instrument}
              </text>
            </svg>

            {/* Legend */}
            <div className="space-y-3 flex-1">
              {slices.map((s, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className="w-3 h-3 rounded-sm mt-0.5 flex-shrink-0" style={{ backgroundColor: s.color }} />
                  <div>
                    <p className="text-base text-white font-medium">
                      {s.pct}% — {s.label}
                    </p>
                    <p className="text-sm text-gray-400">{s.detail}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Hiring plan */}
        <motion.div
          initial={{ opacity: 0, x: 30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
        >
          <h3 className="text-white font-semibold mb-6 flex items-center gap-2">
            <Users className="w-5 h-5 text-[#A78BFA]" />
            Hiring Plan
          </h3>

          <div className="space-y-3">
            {data.hiringPlan.map((h, i) => (
              <div
                key={i}
                className="bg-[#0F1826]/60 border border-white/8 rounded-xl p-4 flex items-start gap-3"
              >
                <div className="w-8 h-8 rounded-lg bg-[#A78BFA]/15 flex items-center justify-center flex-shrink-0">
                  <span className="text-xs font-bold text-[#C4B5FD]">{i + 1}</span>
                </div>
                <div>
                  <p className="text-base text-white font-medium">{h.role}</p>
                  <p className="text-sm text-gray-400">{h.detail}</p>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </PitchSection>
  );
}
